import React from 'react'
import { Component, LocaleCode } from '../types/component'
import { getLocalizedText } from '../utils/localization'
import { cn } from '../lib/utils'

interface TeamProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
}

export function Team({ component, locale, style }: TeamProps) {
  const config = component.config as any

  const title = getLocalizedText(config.title, locale)
  const subtitle = getLocalizedText(config.subtitle, locale)
  const members = config.members || []
  const columns = config.columns || 3

  const gridColsMap = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
  }
  const gridCols = gridColsMap[columns as keyof typeof gridColsMap] || 'md:grid-cols-3'

  return (
    <div style={style} className="w-full">
      <div className="max-w-7xl mx-auto">
        {(title || subtitle) && (
          <div className="text-center mb-12">
            {title && (
              <h2 className="text-3xl font-bold mb-4" style={{ color: style.color }}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-lg opacity-80" style={{ color: style.color }}>
                {subtitle}
              </p>
            )}
          </div>
        )}

        <div className={cn('grid grid-cols-1 gap-8', gridCols)}>
          {members.map((member: any, index: number) => (
            <div
              key={index}
              className="text-center p-6 rounded-lg transition-transform hover:scale-105 shadow-md"
              style={{
                backgroundColor: style.backgroundColor === '#ffffff' || !style.backgroundColor ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.1)',
              }}
            >
              {member.avatar ? (
                <img
                  src={member.avatar}
                  alt={member.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                />
              ) : (
                <div
                  className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl"
                  style={{
                    backgroundColor: style.backgroundColor === '#ffffff' || !style.backgroundColor ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)',
                  }}
                >
                  👤
                </div>
              )}

              <h3 className="text-xl font-semibold mb-1" style={{ color: style.color }}>
                {member.name}
              </h3>

              <p className="text-sm font-medium mb-3 opacity-70" style={{ color: style.color }}>
                {member.role}
              </p>

              {member.bio && (
                <p className="text-sm opacity-60 mb-4" style={{ color: style.color }}>
                  {member.bio}
                </p>
              )}

              {member.social && (
                <div className="flex gap-3 justify-center">
                  {member.social.twitter && (
                    <a
                      href={member.social.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg hover:scale-125 transition-transform no-underline"
                      style={{ color: style.color }}
                    >
                      🐦
                    </a>
                  )}
                  {member.social.linkedin && (
                    <a
                      href={member.social.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg hover:scale-125 transition-transform no-underline"
                      style={{ color: style.color }}
                    >
                      💼
                    </a>
                  )}
                  {member.social.github && (
                    <a
                      href={member.social.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg hover:scale-125 transition-transform no-underline"
                      style={{ color: style.color }}
                    >
                      🐙
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
